import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  TextField,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { grey } from "@mui/material/colors";
import axios from "axios";
import toast from "react-hot-toast";
import MUIDataTable from "mui-datatables";
import Loader from "../../components/loader/Loader";
import { useForm, Controller } from "react-hook-form";
import { FormAutoComplete } from "../../components/form-components/FormAutoComplete";
import { useSelector } from "react-redux";
import { usePermission } from "../../hooks/usePermission";

const SuggestionProduct = () => {
  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [productList, setProductList] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [currentToggleRow, setCurrentToggleRow] = useState(null);
  const permissions = usePermission("Suggestion");
  const { regionList } = useSelector((state) => state.counter);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      product_id: "",
      region: null,
    },
    mode: "onChange",
  });

  const fetchSuggestedProducts = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/suggest-products");

      setFormData(
        (response?.data?.data || []).map((item) => {
          let regionId = item.region_id;
          if (typeof regionId === "object" && regionId !== null) {
            regionId = regionId?.value || regionId?.region_id || regionId;
          }

          return {
            ...item,
            id: item.sugg_id,
            region_id: regionId,
          };
        })
      );
    } catch (err) {
      toast.error(err?.message || "Failed to fetch suggested products.");
    } finally {
      setLoader(false);
    }
  };

  const fetchProductList = async () => {
    try {
      const res = await axios.get("/getProductList");
      const list = res?.data?.data;
      setProductList(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error("Failed to fetch product list.");
      setProductList([]);
    }
  };

  useEffect(() => {
    fetchSuggestedProducts();
    fetchProductList();
  }, []);

  // Only reset the form when all data is ready
  useEffect(() => {
    if (selectedRow && regionList.length > 0 && open) {
      let productId = selectedRow?.product_id;
      if (typeof productId === "object" && productId !== null) {
        productId = productId?.value || productId?.id;
      }

      const region = regionList.find(
        (r) => String(r.region_id) === String(selectedRow.region_id)
      );

      reset({
        product_id: String(productId || ""),
        region: region
          ? {
              value: region.region_id,
              label: region.city?.name || "",
            }
          : null,
      });
    }
  }, [regionList, selectedRow, open, reset]);

  const handleEditRow = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleOpenDialog = () => {
    setSelectedRow(null);
    reset({
      product_id: "",
      region: null,
    });
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedRow(null);
    reset({
      product_id: "",
      region: null,
    });
  };

  const onSubmit = async (data) => {
    try {
      if (!data.region?.value) {
        toast.error("Please select a region.");
        return;
      }

      const payload = {
        region_id: String(data.region.value),
        product_id: String(data.product_id),
        active_status: selectedRow ? 0 : 1,
      };

      if (selectedRow) {
        await axios.put(`/suggest-products/${selectedRow.sugg_id}`, payload);
        toast.success("Product updated successfully!");
      } else {
        await axios.post("/suggest-products", payload);
        toast.success("Product added successfully!");
      }

      handleCloseDialog();
      fetchSuggestedProducts();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save product.");
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const row = formData.find((item) => item.sugg_id === id);
      const newStatus = currentStatus === 1 ? 0 : 1;

      let regionId = row?.region_id;
      if (typeof regionId === "object" && regionId !== null) {
        regionId = regionId?.value || regionId?.region_id;
      }

      let productId = row?.product_id;
      if (typeof productId === "object" && productId !== null) {
        productId = productId?.value || productId?.id;
      }

      const payload = {
        region_id: String(regionId),
        product_id: String(productId),
        active_status: newStatus,
      };

      await axios.put(`/suggest-products/${id}`, payload);
      toast.success("Status updated successfully!");
      fetchSuggestedProducts();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status.");
      fetchSuggestedProducts();
    }
  };

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },
    {
      name: "product_id",
      label: "Product ID",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <p>{formData[dataIndex]?.product_id}</p>
        ),
      },
    },
    {
      name: "name",
      label: "Region",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <p>
            {
              formData[dataIndex]?.products?.[0]?.region?.[0]?.itemName.city
                .name
            }
          </p>
        ),
      },
    },
    {
      name: "products",
      label: "Products",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const products = formData[dataIndex]?.products || [];
          const maxPills = 4;

          const allNames = products
            .map((p) => `${p.product_name} (${p.id})`)
            .join(", ");

          const pills = products.slice(0, maxPills).map((product, idx) => (
            <span
              key={product.id || idx}
              title={allNames}
              style={{
                display: "inline-block",
                background: "#e0e7ff",
                color: "#3730a3",
                borderRadius: "16px",
                padding: "2px 10px",
                marginRight: "4px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {product.product_name} ({product.id})
            </span>
          ));

          const extraCount = products.length - maxPills;

          return (
            <span>
              {pills}
              {extraCount > 0 && (
                <span
                  style={{
                    display: "inline-block",
                    background: "#fca5a5",
                    color: "#991b1b",
                    borderRadius: "16px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  title={allNames}
                >
                  +{extraCount} more
                </span>
              )}
            </span>
          );
        },
      },
    },
    {
      name: "Status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const row = formData[dataIndex];
          const status = row?.active_status;
          const id = row?.sugg_id;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() => {
                setCurrentToggleRow({ id, currentStatus: status });
                setConfirmDialogOpen(true);
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },
    ...(permissions?.edit
      ? [
          {
            name: "Edit",
            label: "Edit",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const row = formData[dataIndex];
                const status = row?.active_status;

                return (
                  <Button
                    disabled={status === 1}
                    aria-label="edit"
                    onClick={() => handleEditRow(row)}
                  >
                    <EditIcon
                      fontSize="medium"
                      sx={{
                        color: status === 1 ? grey[600] : "secondary.main",
                      }}
                    />
                  </Button>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: true,
    viewColumns: true,
    print: false,
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4>SuggestionProduct</h4>
          <MUIDataTable
            title={
              permissions.add ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                  >
                    New Product
                  </Button>
                </div>
              ) : null
            }
            data={formData}
            columns={columns}
            options={options}
          />
        </>
      )}

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedRow ? "Edit Product" : "Add New Product"}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="product_id"
                  control={control}
                  rules={{ required: "Main product is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Select Main Product"
                      fullWidth
                      select
                      SelectProps={{ native: true }}
                      error={!!errors.product_id}
                      helperText={errors.product_id?.message}
                    >
                      <option value=""></option>
                      {productList.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.product_name} ({product.id})
                        </option>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormAutoComplete
                  name="region"
                  control={control}
                  label="Region"
                  multiple={false}
                  dataArray={regionList?.map((item) => ({
                    value: item?.region_id,
                    label: item?.city?.name,
                  }))}
                  errors={errors}
                  mandatory="true"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              variant="contained"
              color="error"
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedRow ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirm status toggle dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {currentToggleRow?.currentStatus === 1 ? "deactivate" : "activate"}{" "}
            this product?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              toggleStatus(currentToggleRow.id, currentToggleRow.currentStatus);
              setConfirmDialogOpen(false);
            }}
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SuggestionProduct;
